import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, XCircle, Clock, ShieldCheck } from 'lucide-react';

import { getPublicFeedbackForm, submitPublicFeedback } from './api';
import TextInput from '../../components/ui/TextInput.jsx';
import Button from '../../components/ui/Button';
import DecisionRadio from '../period-goals/DecisionRadio';

// P1 Görev Paketi E — harici (şirket dışı) 360° değerlendirici doldurma sayfası.
// Rota: /degerlendirme/:token — LOGIN'SİZ (OfferAcceptPage gibi ProtectedRoute
// DIŞI düz Route; manifestte önerildi). Dil: ?lang= parametresi (tr|en|ru|kk|az,
// varsayılan tr) + sayfa içi seçici. Form soruları GET v1/public/feedback/{token}
// (sorular + değerlendirilen ad + dönem adı + önceki cevaplar), gönderim POST
// aynı uca — pencere içinde yeniden doldurma serbest (upsert-by-replace).
// Hata durumları HTTP kodundan ayrışır: 404 geçersiz bağlantı, 410 süresi
// geçmiş, 409 pencere kapalı. Anonim axios istemcisi (./api) interceptor
// taşımaz — hatalar sayfa içi banner'la gösterilir (toast YOK).
const SUPPORTED_LANGS = ['tr', 'en', 'ru', 'kk', 'az'];

const Page = styled.div`
  min-height: 100vh;
  background: ${({ theme }) => theme.colors.bgColor};
  display: flex;
  flex-direction: column;
`;

const Header = styled.header`
  background: #fff;
  border-bottom: 1px solid #e8ecf0;
  padding: 14px 0;
`;

const HeaderInner = styled.div`
  max-width: 720px;
  margin: 0 auto;
  padding: 0 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const BrandTitle = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.lg};
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const LangSelect = styled.select`
  border: 1px solid #e0e4e8;
  border-radius: 6px;
  padding: 6px 10px;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.primary};
  background: #fff;
`;

const Main = styled.main`
  flex: 1;
  max-width: 720px;
  width: 100%;
  margin: 0 auto;
  padding: 32px 24px 64px;
`;

const Card = styled.section`
  background: #fff;
  border: 1px solid #e8ecf0;
  border-radius: 12px;
  padding: 28px 30px;
  margin-bottom: 16px;
`;

const Title = styled.h1`
  font-size: 22px;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 6px;
`;

const Subtitle = styled.p`
  color: #6c757d;
  font-size: 14px;
  margin: 0 0 4px;
`;

const AnonymityStrip = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: #eff6ff;
  color: #0291ef;
  border-radius: 6px;
  padding: 10px 14px;
  font-size: 12px;
  margin: 14px 0 4px;
`;

const QuestionBlock = styled.div`
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e8ecf0;
  &:last-of-type { border-bottom: 0; padding-bottom: 0; }
`;

const QuestionText = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 10px;
`;

const ScaleHint = styled.div`
  font-size: 11px;
  color: #6c757d;
  margin-top: 6px;
`;

const Banner = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px 20px;
  border-radius: 10px;
  background: ${({ $tone }) => ($tone === 'success' ? '#E8F8F0' : $tone === 'error' ? '#FEE6E6' : '#FFF5E1')};
  color: ${({ $tone }) => ($tone === 'success' ? '#1A6E38' : $tone === 'error' ? '#A02525' : '#8A5500')};
  border: 1px solid ${({ $tone }) => ($tone === 'success' ? '#2EA44F' : $tone === 'error' ? '#E53935' : '#FFC107')};
  font-size: 14px;
  margin-bottom: 16px;
`;

// #72 — yerel SubmitButton kaldırıldı; JSX artık paylaşılan `<Button
// fullWidth>` kullanıyor (bkz. t6-report.md).

const LIKERT_OPTIONS = [1, 2, 3, 4, 5].map((n) => ({ value: n, label: String(n) }));

// HTTP durum kodu → hata ekranı türü (servis anahtarları:
// performance.feedback.tokenInvalid|tokenExpired|windowClosed).
const errorTypeFromStatus = (status) => {
  if (status === 410) return 'expired';
  if (status === 409) return 'windowClosed';
  return 'invalid';
};

export default function PublicFeedbackPage() {
  const { token } = useParams();
  const { search } = useLocation();
  const { t, i18n } = useTranslation();

  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorType, setErrorType] = useState(null); // invalid | expired | windowClosed
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // ?lang= parametresi (tr|en|ru|kk|az; varsayılan tr) — harici değerlendirici
  // login olmadığından tenant/kullanıcı dili yoktur, davet linki dili taşır.
  useEffect(() => {
    const lang = new URLSearchParams(search).get('lang');
    i18n.changeLanguage(SUPPORTED_LANGS.includes(lang) ? lang : 'tr');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setLoading(true);
    getPublicFeedbackForm(token)
      .then((res) => {
        const data = res.data?.data;
        if (!data) { setErrorType('invalid'); return; }
        setForm(data);
        const initial = {};
        (data.existingAnswers || []).forEach((a) => {
          initial[a.questionId] = { likertValue: a.likertValue, textValue: a.textValue };
        });
        setAnswers(initial);
      })
      .catch((err) => setErrorType(errorTypeFromStatus(err?.response?.status)))
      .finally(() => setLoading(false));
  }, [token]);

  const questions = useMemo(() => {
    if (!form?.questions) return [];
    return [...form.questions].sort((a, b) => a.sortOrder - b.sortOrder);
  }, [form]);

  const setLikertValue = (questionId, value) => {
    setAnswers((prev) => ({ ...prev, [questionId]: { ...prev[questionId], likertValue: value } }));
  };
  const setTextValue = (questionId, value) => {
    setAnswers((prev) => ({ ...prev, [questionId]: { ...prev[questionId], textValue: value } }));
  };

  const allLikertAnswered = questions
    .filter((q) => q.questionType === 'likert')
    .every((q) => {
      const v = answers[q.id]?.likertValue;
      return v >= 1 && v <= 5;
    });

  const payloadAnswers = useMemo(() => questions
    .map((q) => {
      const a = answers[q.id] || {};
      if (q.questionType === 'likert' && a.likertValue) {
        return { questionId: q.id, likertValue: a.likertValue };
      }
      if (q.questionType === 'open_text' && a.textValue && a.textValue.trim()) {
        return { questionId: q.id, textValue: a.textValue.trim() };
      }
      return null;
    })
    .filter(Boolean), [questions, answers]);

  const canSubmit = questions.length > 0 && allLikertAnswered && payloadAnswers.length > 0;

  const handleSubmit = async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await submitPublicFeedback(token, { answers: payloadAnswers });
      if (res.data?.status) setSubmitted(true);
      else setSubmitError('generic');
    } catch (err) {
      const status = err?.response?.status;
      if (status === 410 || status === 409) setErrorType(errorTypeFromStatus(status));
      else setSubmitError('generic');
    } finally {
      setSubmitting(false);
    }
  };

  const handleLangChange = (e) => i18n.changeLanguage(e.target.value);

  const header = (
    <Header>
      <HeaderInner>
        <BrandTitle>{t('publicFeedback.title')}</BrandTitle>
        <LangSelect value={i18n.language} onChange={handleLangChange} aria-label={t('publicFeedback.languageLabel')}>
          {SUPPORTED_LANGS.map((l) => <option key={l} value={l}>{l.toUpperCase()}</option>)}
        </LangSelect>
      </HeaderInner>
    </Header>
  );

  if (loading) {
    return <Page>{header}<Main><Card>{t('publicFeedback.loading')}</Card></Main></Page>;
  }

  if (errorType) {
    const bannerText = errorType === 'expired'
      ? t('publicFeedback.expiredLink')
      : errorType === 'windowClosed'
        ? t('publicFeedback.windowClosed')
        : t('publicFeedback.invalidLink');
    return (
      <Page>
        {header}
        <Main>
          <Card>
            <Banner $tone={errorType === 'invalid' ? 'error' : 'warning'}>
              {errorType === 'expired' ? <Clock size={20} /> : <XCircle size={20} />}
              {bannerText}
            </Banner>
          </Card>
        </Main>
      </Page>
    );
  }

  if (submitted) {
    return (
      <Page>
        {header}
        <Main>
          <Card>
            <Banner $tone="success">
              <CheckCircle2 size={20} />
              {t('publicFeedback.thanksTitle')}
            </Banner>
            <Subtitle>{t('publicFeedback.thanksBody')}</Subtitle>
            <Subtitle>{t('publicFeedback.editAgainHint')}</Subtitle>
          </Card>
        </Main>
      </Page>
    );
  }

  return (
    <Page>
      {header}
      <Main>
        <Card>
          <Title>{t('publicFeedback.formTitle', { employee: form.employeeName })}</Title>
          <Subtitle>{t('publicFeedback.intro', { employee: form.employeeName, period: form.periodName })}</Subtitle>
          {form.status === 'completed' && (
            <Banner $tone="warning" style={{ marginTop: 12 }}>
              <CheckCircle2 size={18} />
              {t('publicFeedback.alreadyFilled')}
            </Banner>
          )}
          <AnonymityStrip>
            <ShieldCheck size={16} />
            {t('publicFeedback.anonymityNote')}
          </AnonymityStrip>
        </Card>

        <Card>
          {questions.map((q) => (
            <QuestionBlock key={q.id}>
              <QuestionText>{q.text}</QuestionText>
              {q.questionType === 'likert' ? (
                <>
                  <DecisionRadio
                    name={`likert-${q.id}`}
                    value={answers[q.id]?.likertValue || null}
                    onChange={(v) => setLikertValue(q.id, v)}
                    options={LIKERT_OPTIONS}
                  />
                  <ScaleHint>{t('publicFeedback.likertScaleHint')}</ScaleHint>
                </>
              ) : (
                <TextInput
                  type="textarea"
                  rows={3}
                  value={answers[q.id]?.textValue || ''}
                  onChange={(e) => setTextValue(q.id, e.target.value)}
                />
              )}
            </QuestionBlock>
          ))}
        </Card>

        <Card>
          {submitError && (
            <Banner $tone="error">
              <XCircle size={20} />
              {t('publicFeedback.submitError')}
            </Banner>
          )}
          <Button fullWidth onClick={handleSubmit} disabled={!canSubmit || submitting}>
            <CheckCircle2 size={16} />
            {t('publicFeedback.submit')}
          </Button>
        </Card>
      </Main>
    </Page>
  );
}
